/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useRef, useState } from 'react';

export function useClickInside(
  elRef: React.RefObject<any>,
  callback: (e: MouseEvent) => void
): void {
  const callbackRef = useRef<(e: MouseEvent) => void>();
  callbackRef.current = callback;

  useEffect(() => {
    const handleClickInside = (e: MouseEvent): void => {
      if (
        elRef.current &&
        elRef.current.contains(e.target) &&
        callbackRef.current
      ) {
        callbackRef.current(e);
      }
    };

    document.addEventListener('click', handleClickInside, true);
    return () => {
      document.removeEventListener('click', handleClickInside, true);
    };
  }, [callbackRef, elRef]);
}

export function useClickOutside(
  elRef: React.RefObject<any>,
  callback: (e: MouseEvent) => void
): void {
  const callbackRef = useRef<(e: MouseEvent) => void>();
  callbackRef.current = callback;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (
        elRef.current &&
        !elRef.current.contains(e.target) &&
        callbackRef.current
      ) {
        callbackRef.current(e);
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [callbackRef, elRef]);
}

export function useKeyPress(
  targetKey: string | number,
  onKeyPressNonUpdatingOnRerenderCallback: () => void
): boolean {
  const [keyPressed, setKeyPressed] = useState(false);

  const downHandler = ({ key, keyCode }: KeyboardEvent): void => {
    if (key === targetKey || keyCode === targetKey) {
      onKeyPressNonUpdatingOnRerenderCallback();
      setKeyPressed(true);
    }
  };

  const upHandler = ({ key, keyCode }: KeyboardEvent): void => {
    if (key === targetKey || keyCode === targetKey) {
      setKeyPressed(false);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);

    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return keyPressed;
}
